import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { EXTENSIONS, STATUS_LABEL } from "@/lib/extensions";
import { toast } from "sonner";
import { Check, X, FileText, Download } from "lucide-react";

const TABS = ["verifications", "listings", "interests", "leads", "fleet", "users", "emails"];
const KIND = { seller_ownership: "Titularidad · Vendedor", owner_via_b: "Alta Vía B · Propietario", renter_insurance: "Seguro RC · Alquiler", identity: "Identidad" };

function Verifications() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [notes, setNotes] = useState({});
  const load = () => { api.get(`/admin/verifications?status=${filter}`).then((r) => setItems(r.data)); };
  useEffect(load, [filter]);

  const decide = async (id, decision) => {
    await api.post(`/admin/verifications/${id}/decide`, { decision, note: notes[id] || "" });
    toast.success(decision === "approved" ? "Aprobada" : "Rechazada"); load();
  };
  const download = async (docId, name) => {
    const r = await api.get(`/profile/documents/${docId}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data); const el = document.createElement("a"); el.href = url; el.download = name; el.click();
  };

  return (
    <div data-testid="admin-verifications">
      <div className="flex gap-2 mb-6">
        {["pending", "approved", "rejected", "all"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`px-4 py-1.5 rounded-full text-sm border ${filter === f ? "bg-[#0B192C] text-white border-[#0B192C]" : "border-slate-200"}`} data-testid={`admin-ver-filter-${f}`}>{f}</button>
        ))}
      </div>
      {items.length === 0 && <p className="text-slate-500 text-sm" data-testid="admin-ver-empty">Sin verificaciones.</p>}
      <div className="space-y-5">
        {items.map((v) => (
          <div key={v.id} className="card-clean p-6" data-testid={`admin-ver-${v.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="eyebrow">{KIND[v.kind] || v.kind}</p>
                <p className="font-serif text-xl text-[#0B192C] mt-1">{v.user?.first_name} {v.user?.last_name} <span className="text-sm text-slate-400 font-body">· {v.user?.email} · {v.user?.phone}</span></p>
                <p className="text-xs text-slate-400 mt-1">{new Date(v.created_at).toLocaleString("es-ES")} · {v.status}</p>
              </div>
            </div>
            {v.extension && (
              <pre className="mt-4 text-xs bg-[#F8F9FA] rounded-xl p-4 overflow-x-auto text-slate-600">{JSON.stringify(v.extension.data, null, 2)}</pre>
            )}
            {v.documents?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {v.documents.map((d) => (
                  <button key={d.id} onClick={() => download(d.id, d.filename)} className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-400" data-testid={`admin-doc-${d.id}`}>
                    <FileText className="h-3.5 w-3.5" /> {d.kind} · {d.filename} <Download className="h-3 w-3" />
                  </button>
                ))}
              </div>
            )}
            {v.status === "pending" && (
              <div className="mt-5 flex items-center gap-3 flex-wrap">
                <input className="input-elegant flex-1 min-w-[200px]" placeholder="Nota (opcional, se muestra al usuario si rechazas)" value={notes[v.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [v.id]: e.target.value }))} data-testid={`admin-ver-note-${v.id}`} />
                <button onClick={() => decide(v.id, "approved")} className="btn-primary text-sm" data-testid={`admin-ver-approve-${v.id}`}><Check className="h-4 w-4" /> Aprobar</button>
                <button onClick={() => decide(v.id, "rejected")} className="btn-outline text-sm" data-testid={`admin-ver-reject-${v.id}`}><X className="h-4 w-4" /> Rechazar</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Listings() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending_review");
  const [notes, setNotes] = useState({});
  const load = () => { api.get(`/admin/listings?status=${filter}`).then((r) => setItems(r.data)); };
  useEffect(load, [filter]);

  const decide = async (id, decision) => {
    await api.post(`/admin/listings/${id}/decide`, { decision, note: notes[id] || "" });
    toast.success(decision === "published" ? "Publicada en Explora" : "Publicación rechazada"); load();
  };

  const FILTERS = ["pending_review", "published", "rejected", "unlisted", "all"];
  return (
    <div data-testid="admin-listings">
      <div className="flex gap-2 mb-6 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`px-4 py-1.5 rounded-full text-sm border ${filter === f ? "bg-[#0B192C] text-white border-[#0B192C]" : "border-slate-200"}`} data-testid={`admin-listing-filter-${f}`}>{t.boats.listing[f] || f}</button>
        ))}
      </div>
      {items.length === 0 && <p className="text-slate-500 text-sm" data-testid="admin-listing-empty">Sin embarcaciones.</p>}
      <div className="space-y-5">
        {items.map((b) => (
          <div key={b.id} className="card-clean p-6" data-testid={`admin-listing-${b.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="eyebrow">{t.boats.listing[b.listing_status] || b.listing_status} · {t.boats.ownershipModes[b.ownership_mode] || "Copropiedad"}</p>
                <p className="font-serif text-xl text-[#0B192C] mt-1">{b.brand} {b.model} <span className="text-sm text-slate-400 font-body">· {b.location || "sin zona"}{b.port ? ` · ${b.port}` : ""} · {b.length ? `${b.length} m` : ""}</span></p>
                <p className="text-xs text-slate-400 mt-1">Propietario: {b.owner?.first_name} {b.owner?.last_name} · {b.owner?.email} · Valor €{Number(b.estimated_value || 0).toLocaleString()} · Participación €{Number(b.ownership?.standard_share_price || 0).toLocaleString()}</p>
              </div>
            </div>
            {b.listing_status === "pending_review" && (
              <div className="mt-5 flex items-center gap-3 flex-wrap">
                <input className="input-elegant flex-1 min-w-[200px]" placeholder="Nota (opcional)" value={notes[b.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [b.id]: e.target.value }))} data-testid={`admin-listing-note-${b.id}`} />
                <button onClick={() => decide(b.id, "published")} className="btn-primary text-sm" data-testid={`admin-listing-approve-${b.id}`}><Check className="h-4 w-4" /> Publicar</button>
                <button onClick={() => decide(b.id, "rejected")} className="btn-outline text-sm" data-testid={`admin-listing-reject-${b.id}`}><X className="h-4 w-4" /> Rechazar</button>
              </div>
            )}
            {b.listing_status === "published" && (
              <div className="mt-5">
                <button onClick={() => decide(b.id, "rejected")} className="btn-outline text-sm" data-testid={`admin-listing-unpublish-${b.id}`}><X className="h-4 w-4" /> Retirar de Explora</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Interests() {
  const [data, setData] = useState({ items: [], match_count: 0, config: {} });
  const [cfg, setCfg] = useState({ zone_weight: 50, price_weight: 50, price_tolerance: 25 });
  const [savingCfg, setSavingCfg] = useState(false);

  const load = () => api.get("/admin/interests").then((r) => {
    setData(r.data);
    const c = r.data.config || {};
    setCfg({ zone_weight: c.zone_weight ?? 50, price_weight: c.price_weight ?? 50, price_tolerance: Math.round((c.price_tolerance ?? 0.25) * 100) });
  });
  useEffect(() => { load(); }, []);

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      await api.put("/admin/matching-config", {
        zone_weight: Number(cfg.zone_weight), price_weight: Number(cfg.price_weight),
        price_tolerance: Number(cfg.price_tolerance) / 100,
      });
      toast.success("Configuración de matching guardada"); load();
    } finally { setSavingCfg(false); }
  };

  const setStatus = async (id, status) => { await api.patch(`/admin/interests/${id}`, { status }); load(); };

  const fmtEur = (n) => `€${Number(n || 0).toLocaleString("es-ES")}`;
  const STATUS = { new: "Nuevo", contacted: "Contactado", closed: "Cerrado" };

  return (
    <div data-testid="admin-interests">
      {/* Config de matching */}
      <div className="card-clean p-6 mb-6" data-testid="admin-matching-config">
        <p className="eyebrow">Configuración de matching</p>
        <p className="text-sm text-slate-500 mt-1">Pesos y tolerancia del cálculo de compatibilidad (zona + precio de participación).</p>
        <div className="mt-4 grid sm:grid-cols-3 gap-4">
          <div>
            <label className="label-elegant">Peso zona</label>
            <input type="number" className="input-elegant" value={cfg.zone_weight} onChange={(e) => setCfg((c) => ({ ...c, zone_weight: e.target.value }))} data-testid="cfg-zone-weight" />
          </div>
          <div>
            <label className="label-elegant">Peso precio</label>
            <input type="number" className="input-elegant" value={cfg.price_weight} onChange={(e) => setCfg((c) => ({ ...c, price_weight: e.target.value }))} data-testid="cfg-price-weight" />
          </div>
          <div>
            <label className="label-elegant">Tolerancia precio (%)</label>
            <input type="number" className="input-elegant" value={cfg.price_tolerance} onChange={(e) => setCfg((c) => ({ ...c, price_tolerance: e.target.value }))} data-testid="cfg-price-tolerance" />
          </div>
        </div>
        <button onClick={saveCfg} disabled={savingCfg} className="btn-outline text-sm mt-4 disabled:opacity-40" data-testid="cfg-save">Guardar configuración</button>
      </div>

      <p className="text-sm text-slate-600 mb-5" data-testid="admin-match-count">
        <span className="font-serif text-2xl text-[#0B192C]">{data.match_count}</span> coincidencias detectadas · {data.items.length} intereses en total
      </p>

      {data.items.length === 0 && <p className="text-slate-500 text-sm" data-testid="admin-interests-empty">Aún no hay intereses.</p>}
      <div className="space-y-4">
        {data.items.map((it) => (
          <div key={it.id} className={`card-clean p-6 ${it.is_match ? "ring-1 ring-[#C2A878]" : ""}`} data-testid={`admin-interest-${it.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-serif text-xl text-[#0B192C]">{it.user.first_name} {it.user.last_name}</p>
                  {it.is_match && <span className="text-[10px] font-display uppercase tracking-widest px-2 py-1 rounded-full bg-[#C2A878] text-white" data-testid={`admin-interest-match-${it.id}`}>Match {it.compatibility}%</span>}
                  {!it.is_match && <span className="text-[10px] font-display uppercase tracking-widest px-2 py-1 rounded-full bg-slate-100 text-slate-500">Compatibilidad {it.compatibility}%</span>}
                </div>
                <p className="text-sm text-slate-500 mt-1">{it.user.email}{it.user.phone ? ` · ${it.user.phone}` : ""}</p>
                <p className="text-xs text-slate-400 mt-1">Zona usuario: {(it.user.zones || []).join(", ") || "—"} · Presupuesto: {it.budget.min ? fmtEur(it.budget.min) : "—"}–{it.budget.max ? fmtEur(it.budget.max) : "—"}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400">{new Date(it.created_at).toLocaleString("es-ES")}</p>
                <select value={it.status} onChange={(e) => setStatus(it.id, e.target.value)} className="input-elegant mt-2 text-sm py-1" data-testid={`admin-interest-status-${it.id}`}>
                  {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-4 rounded-xl bg-[#F8F9FA] p-4 text-sm text-slate-600 flex flex-wrap gap-x-8 gap-y-1">
              <span><b className="text-[#0B192C] font-normal">Barco:</b> {it.boat.brand} {it.boat.model}</span>
              <span><b className="text-[#0B192C] font-normal">Zona barco:</b> {it.boat.zone || "—"}</span>
              <span><b className="text-[#0B192C] font-normal">Participación:</b> {fmtEur(it.share_price)}</span>
              <span><b className="text-[#0B192C] font-normal">Zona coincide:</b> {it.zone_match ? "Sí" : "No"}</span>
              <span><b className="text-[#0B192C] font-normal">Precio compatible:</b> {it.price_compatible === null ? "—" : it.price_compatible ? "Sí" : "No"}</span>
            </div>
            <p className="mt-2 text-xs text-slate-500">Motivo: {it.reason}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Leads() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/interest-requests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.patch(`/interest-requests/${id}`, { status }); load(); };
  const STATUS = { new: "Nuevo", contacted: "Contactado", closed: "Cerrado" };
  const fmt = (n) => (n ? `€${Number(n).toLocaleString("es-ES")}` : "—");

  return (
    <div data-testid="admin-leads">
      <p className="text-sm text-slate-600 mb-5">
        <span className="font-serif text-2xl text-[#0B192C]">{items.length}</span> solicitudes desde el cuestionario de Explora
      </p>
      {items.length === 0 && <p className="text-slate-500 text-sm" data-testid="admin-leads-empty">Aún no hay solicitudes.</p>}
      <div className="space-y-4">
        {items.map((l) => (
          <div key={l.id} className="card-clean p-6" data-testid={`admin-lead-${l.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="font-serif text-xl text-[#0B192C]">{l.full_name}</p>
                <p className="text-sm text-slate-500 mt-1">
                  {l.email}{l.phone ? ` · ${l.phone}` : ""}{l.postal_code ? ` · CP ${l.postal_code}` : ""}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400">{new Date(l.created_at).toLocaleString("es-ES")}</p>
                <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)} className="input-elegant mt-2 text-sm py-1" data-testid={`admin-lead-status-${l.id}`}>
                  {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-4 rounded-xl bg-[#F8F9FA] p-4 text-sm text-slate-600 flex flex-wrap gap-x-8 gap-y-1">
              <span><b className="text-[#0B192C] font-normal">Barco:</b> {l.boat_snapshot?.brand} {l.boat_snapshot?.model}</span>
              <span><b className="text-[#0B192C] font-normal">Zona:</b> {l.boat_snapshot?.location || "—"}</span>
              <span><b className="text-[#0B192C] font-normal">Valor:</b> {fmt(l.boat_snapshot?.estimated_value)}</span>
              <span><b className="text-[#0B192C] font-normal">Participación:</b> {fmt(l.boat_snapshot?.estimated_value ? l.boat_snapshot.estimated_value / 4 : null)}</span>
            </div>
            {l.comments && <p className="mt-3 text-sm text-slate-500"><b className="text-[#0B192C] font-normal">Comentarios:</b> {l.comments}</p>}
            <Link to={`/explorar/${l.boat_id}`} className="mt-3 inline-block text-sm text-[#0B192C] underline underline-offset-4" data-testid={`admin-lead-boat-${l.id}`}>Ver ficha del barco</Link>
          </div>
        ))}
      </div>
    </div>
  );
}

function Fleet() {
  const [data, setData] = useState({ averages: {}, boats: [], year: "" });
  useEffect(() => { api.get("/management/admin/fleet-stats").then((r) => setData(r.data)).catch(() => {}); }, []);
  const a = data.averages || {};
  const eur = (n) => `€${Number(n || 0).toLocaleString("es-ES")}`;
  return (
    <div data-testid="admin-fleet">
      <p className="text-sm text-slate-600 mb-5">Estudio de flota {data.year} · datos agregados de todas las embarcaciones (fuente única, sin duplicar).</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {[["Barcos con actividad", a.boats ?? 0], ["Ingreso medio", eur(a.avg_income)], ["Coste medio", eur(a.avg_costs)], ["Días alquiler medios", a.avg_rental_days ?? 0], ["Días uso medios", a.avg_use_days ?? 0]].map(([k, v]) => (
          <div key={k} className="card-clean p-4"><p className="eyebrow">{k}</p><p className="font-serif text-2xl text-[#0B192C] mt-1">{v}</p></div>
        ))}
      </div>
      <div className="rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F9FA] text-slate-400 text-xs uppercase tracking-wide"><tr><th className="text-left px-4 py-3">Embarcación</th><th className="px-4 py-3">Zona</th><th className="text-right px-4 py-3">Ingresos</th><th className="text-right px-4 py-3">Costes</th><th className="text-right px-4 py-3">Resultado</th><th className="text-right px-4 py-3">Uso</th><th className="text-right px-4 py-3">Alquiler</th></tr></thead>
          <tbody>
            {data.boats.map((b) => (
              <tr key={b.boat_id} className="border-t border-slate-100" data-testid={`fleet-row-${b.boat_id}`}>
                <td className="px-4 py-3 text-[#0B192C]">{b.name}</td><td className="px-4 py-3 text-slate-500">{b.zone}</td>
                <td className="px-4 py-3 text-right text-emerald-700">{eur(b.income)}</td><td className="px-4 py-3 text-right text-red-600">− {eur(b.costs)}</td>
                <td className="px-4 py-3 text-right text-[#0B192C]">{eur(b.result)}</td><td className="px-4 py-3 text-right">{b.use_days}</td><td className="px-4 py-3 text-right">{b.rental_days}</td>
              </tr>
            ))}
            {data.boats.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">Sin datos de actividad todavía.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Users() {
  const { lang } = useLang();
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)); }, []);
  return (
    <div className="overflow-x-auto" data-testid="admin-users">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs font-display uppercase tracking-widest text-slate-400 border-b border-slate-200">
          <th className="py-3 pr-4">Usuario</th><th className="py-3 pr-4">Contacto</th><th className="py-3 pr-4">Zonas</th><th className="py-3 pr-4">Extensiones</th><th className="py-3">Identidad</th></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-slate-100" data-testid={`admin-user-${u.id}`}>
              <td className="py-3 pr-4 font-display text-[#0B192C]">{u.first_name} {u.last_name}{u.role === "admin" && <span className="ml-2 text-[10px] text-[#C2A878]">ADMIN</span>}</td>
              <td className="py-3 pr-4 text-slate-500">{u.email}<br />{u.phone}</td>
              <td className="py-3 pr-4 text-slate-500">{(u.nautical_zones || []).join(", ")}</td>
              <td className="py-3 pr-4">
                <div className="flex flex-wrap gap-1">
                  {(u.extensions || []).map((e) => <span key={e.type} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F8F9FA] border border-slate-200">{EXTENSIONS[e.type].title[lang]} · {STATUS_LABEL[e.status][lang]}</span>)}
                </div>
              </td>
              <td className="py-3 text-slate-500">{u.identity_status || "none"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Emails() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/email-log").then((r) => setItems(r.data)); }, []);
  return (
    <div data-testid="admin-emails">
      {items.length === 0 && <p className="text-slate-500 text-sm">Sin emails registrados.</p>}
      <div className="space-y-3">
        {items.map((e) => (
          <div key={e.id} className="card-clean p-4 text-sm flex items-start justify-between gap-4 flex-wrap" data-testid={`admin-email-${e.id}`}>
            <div><p className="font-display text-[#0B192C]">{e.template} → {e.to}</p><p className="text-xs text-slate-400 mt-1">{JSON.stringify(e.data)}</p></div>
            <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#F8F9FA] border border-slate-200">{e.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("verifications");
  const LABEL = { verifications: "Verificaciones", listings: "Publicaciones", interests: "Intereses", leads: "Leads (Explora)", fleet: "Flota", users: "Usuarios", emails: "Emails" };
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20" data-testid="admin-page">
      <p className="eyebrow">Backoffice</p>
      <h1 className="font-serif text-4xl lg:text-5xl mt-3 text-[#0B192C]" data-testid="admin-title">Administración</h1>
      <div className="mt-10 flex gap-6 border-b border-slate-200">
        {TABS.map((k) => (
          <button key={k} onClick={() => setTab(k)} className={`pb-3 font-display text-sm border-b-2 -mb-px transition-colors ${tab === k ? "border-[#0B192C] text-[#0B192C]" : "border-transparent text-slate-400 hover:text-slate-700"}`} data-testid={`admin-tab-${k}`}>{LABEL[k]}</button>
        ))}
      </div>
      <div className="mt-8">
        {tab === "verifications" && <Verifications />}
        {tab === "listings" && <Listings />}
        {tab === "interests" && <Interests />}
        {tab === "leads" && <Leads />}
        {tab === "fleet" && <Fleet />}
        {tab === "users" && <Users />}
        {tab === "emails" && <Emails />}
      </div>
    </div>
  );
}
